import { useLoadingAnimation } from '@/components/LoadingAnimation/useLoadingAnimation.hook';
import { DEFAULT_ERROR_MESSAGE, FILE_TOO_LARGE_ERROR_MESSAGE } from '@/config/text.const';
import { HttpStatusCode } from '@/services/http/HttpStatusCodes';
import { upliftHttpClient } from '@/services/http/clients/upliftHttpClient';
import { notificationService } from '@/services/notification/notification.client';

export type UploadUpliftFileFields = {
  effectiveDate: Date | undefined;
  fileUpload: File | undefined;
};

export interface UploadFileOptions {
  allowDuplicates?: boolean;
  onDuplicateFileName?: () => void;
}

type FileUploadProps = {
  values: UploadUpliftFileFields;
  callback?: Function;
  options?: UploadFileOptions;
};

function useUploadFile() {
  const { toggleLoadingAnimation } = useLoadingAnimation();

  const onFileUpload = async ({ values, callback, options = {} }: FileUploadProps) => {
    const { allowDuplicates = false, onDuplicateFileName } = options;
    const uploadedFile = new FormData();
    if (values.fileUpload && values.effectiveDate) {
      uploadedFile.append('effectiveDate', new Date(values.effectiveDate).toISOString());
      uploadedFile.append('upliftsFile', values.fileUpload);
    }
    toggleLoadingAnimation(true);

    const httpConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: { allowDuplicates }
    };
    try {
      const uploadResult = await upliftHttpClient.post('/Documents/upload', uploadedFile, httpConfig);
      if (uploadResult?.status === HttpStatusCode.OK) {
        callback?.();

        notificationService.open({
          title: 'Success',
          text: 'File uploaded successfully',
          type: 'success'
        });
        notificationService.open({
          title: 'Verifying uploaded file',
          text: 'The uploaded file is being verified',
          type: 'warning'
        });
      }
    } catch (error: any) {
      const isDuplicateError = error.response?.status === HttpStatusCode.BAD_REQUEST
        && error.response?.data?.message?.includes('has already been uploaded');

      if (isDuplicateError && onDuplicateFileName) {
        onDuplicateFileName();
        return;
      }

      let errorMessage = error?.response?.data?.message ?? error?.message ?? DEFAULT_ERROR_MESSAGE;
      if (error.response?.status === HttpStatusCode.PAYLOAD_TOO_LARGE) {
        errorMessage = error.response?.data?.message ?? FILE_TOO_LARGE_ERROR_MESSAGE;
      }
      notificationService.open({
        title: 'Uploading failed',
        text: errorMessage,
        type: 'danger'
      });
    } finally {
      toggleLoadingAnimation(false);
    }
  };

  return {
    onFileUpload
  };
}

export default useUploadFile;
